import React from 'react'
import { motion } from 'framer-motion'
import { Pie } from 'react-chartjs-2'
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'
import { format } from 'date-fns'

ChartJS.register(ArcElement, Tooltip, Legend)

const DCAPositionResult = ({ result }) => {
  if (!result) return null

  const data = result
  const formattedDate = format(new Date(data.timestamp), 'PPpp')

  const pieData = {
    labels: ['Initial BTC', 'Additional BTC'],
    datasets: [
      {
        label: 'BTC Breakdown',
        data: [Math.abs(data.initial_btc), Math.abs(data.additional_btc)],
        backgroundColor: ['#3b82f6', '#f97316'],
        borderColor: '#1f2937',
        borderWidth: 1,
      },
    ],
  }

  const pieOptions = {
    plugins: {
      legend: {
        position: 'bottom',
        labels: { color: '#fff', font: { size: 13 } }
      },
      tooltip: {
        callbacks: {
          label: ctx => `${ctx.label}: ${ctx.raw.toFixed(4)} BTC`
        }
      }
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="rounded-2xl p-6 shadow-xl bg-slate-800/50 text-white backdrop-blur-lg border border-white/10"
    >
      <h2 className="text-2xl font-bold mb-4">DCA Position Adjustment</h2>
      <p className="text-sm text-gray-300 mb-6">
        Data reflects the most recent update as of: {formattedDate}
      </p>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <InfoBlock label="Exchange" value={data.exchange} />
        <InfoBlock label="Symbol" value={`${data.symbol}/${data.quote_currency}`} />
        <InfoBlock label="Initial BTC" value={`${data.initial_btc} BTC`} />
        <InfoBlock label="Additional BTC" value={`${data.additional_btc} BTC`} />
        <InfoBlock label="Additional Investment" value={`$${data.additional_investment}`} />
        <InfoBlock label="Fee Paid" value={`$${data.fee_paid}`} />
        <InfoBlock label="Effective Add. Invest." value={`$${data.effective_additional_investment}`} />
        <InfoBlock label="Total BTC After Adj." value={`${data.total_btc} BTC`} />
        <InfoBlock label="Total Investment" value={`$${data.total_investment}`} />
        <InfoBlock label="New Avg Price" value={`$${data.new_average_price.toFixed(2)}`} />
        <div>
          <p className="text-sm text-gray-400">Target Achieved</p>
          <p className={`font-semibold inline-block px-2 py-1 rounded text-sm mt-1 ${data.target_achieved ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
            {data.target_achieved ? 'Yes ✅' : 'No ❌'}
          </p>
        </div>
      </div>

      <div className="max-w-xs mx-auto mt-4 mb-6">
        <Pie data={pieData} options={pieOptions} />
      </div>

      <p className="text-xs text-gray-300 mt-6">
        This is tentative data and may not reflect precise values.
      </p>
    </motion.div>
  )
}

const InfoBlock = ({ label, value }) => (
  <div>
    <p className="text-sm text-gray-400">{label}</p>
    <p className="font-semibold capitalize">{value}</p>
  </div>
)

export default DCAPositionResult
